/**
 * Blog Command Center Service
 * 
 * Comprehensive service for AI blog generation, SEO optimization,
 * scheduling, analytics, and content management.
 */

import { supabase } from './supabaseClient';

// =============================================================================
// COMMAND CENTER STATS
// =============================================================================

/**
 * Get comprehensive stats for the blog command center
 */
export async function getCommandCenterStats() {
    const { data, error } = await supabase.rpc('get_blog_command_center_stats');

    if (error) {
        console.error('Error fetching command center stats:', error);
        throw error;
    }

    return data;
}

// =============================================================================
// SCHEDULING
// =============================================================================

/**
 * Get the current schedule configuration
 */
export async function getScheduleConfig() {
    const { data, error } = await supabase
        .from('blog_schedule')
        .select('*')
        .limit(1)
        .single();

    if (error && error.code !== 'PGRST116') {
        console.error('Error fetching schedule:', error);
        throw error;
    }

    return data;
}

/**
 * Update schedule configuration
 */
export async function updateScheduleConfig(config) {
    // First check if schedule exists
    const existing = await getScheduleConfig();

    if (existing) {
        const { data, error } = await supabase
            .from('blog_schedule')
            .update({
                ...config,
                updated_at: new Date().toISOString()
            })
            .eq('id', existing.id)
            .select()
            .single();

        if (error) throw error;
        return data;
    } else {
        const { data, error } = await supabase
            .from('blog_schedule')
            .insert(config)
            .select()
            .single();

        if (error) throw error;
        return data;
    }
}

/**
 * Toggle schedule enabled/disabled
 */
export async function toggleSchedule(enabled) {
    const existing = await getScheduleConfig();

    if (!existing) {
        throw new Error('No schedule configuration found');
    }

    const nextRunAt = enabled ? calculateNextRunTime(existing) : null;

    const { data, error } = await supabase
        .from('blog_schedule')
        .update({
            is_enabled: enabled,
            next_run_at: nextRunAt,
            updated_at: new Date().toISOString()
        })
        .eq('id', existing.id)
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Calculate next run time based on schedule config
 */
function calculateNextRunTime(schedule) {
    const now = new Date();
    const [hours, minutes] = schedule.time_of_day.split(':').map(Number);

    let nextRun = new Date();
    nextRun.setHours(hours, minutes, 0, 0);

    // If time has passed today, move to next valid day
    if (nextRun <= now) {
        nextRun.setDate(nextRun.getDate() + 1);
    }

    // Find next valid day of week
    if (schedule.days_of_week && schedule.days_of_week.length > 0) {
        let attempts = 0;
        while (!schedule.days_of_week.includes(nextRun.getDay()) && attempts < 7) {
            nextRun.setDate(nextRun.getDate() + 1);
            attempts++;
        }
    }

    return nextRun.toISOString();
}

// =============================================================================
// GENERATION HISTORY
// =============================================================================

/**
 * Get generation history with pagination
 */
export async function getGenerationHistory({
    page = 1,
    limit = 20,
    status = null,
    triggerType = null
}) {
    let query = supabase
        .from('blog_generations')
        .select(`
      *,
      topic:blog_topics(id, title),
      post:blog_posts(id, title, slug)
    `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

    if (status) {
        query = query.eq('status', status);
    }

    if (triggerType) {
        query = query.eq('trigger_type', triggerType);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return {
        items: data,
        total: count,
        page,
        totalPages: Math.ceil(count / limit)
    };
}

/**
 * Create a new generation request
 */
export async function createGenerationRequest({
    topicId,
    triggerType = 'manual',
    metadata = {}
}) {
    const { data, error } = await supabase
        .from('blog_generations')
        .insert({
            topic_id: topicId,
            trigger_type: triggerType,
            status: 'pending',
            metadata
        })
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Update generation status
 */
export async function updateGenerationStatus(generationId, updates) {
    const { data, error } = await supabase
        .from('blog_generations')
        .update(updates)
        .eq('id', generationId)
        .select()
        .single();

    if (error) throw error;
    return data;
}

// =============================================================================
// CONTENT QUEUE
// =============================================================================

/**
 * Get content queue items
 */
export async function getContentQueue({
    page = 1,
    limit = 20,
    status = null,
    sortBy = 'priority'
}) {
    let query = supabase
        .from('blog_content_queue')
        .select(`
      *,
      topic:blog_topics(id, title),
      post:blog_posts(id, title, slug)
    `, { count: 'exact' });

    if (status) {
        if (Array.isArray(status)) {
            query = query.in('status', status);
        } else {
            query = query.eq('status', status);
        }
    }

    if (sortBy === 'priority') {
        query = query.order('priority', { ascending: false });
    } else if (sortBy === 'scheduled') {
        query = query.order('scheduled_for', { ascending: true, nullsFirst: false });
    } else {
        query = query.order('created_at', { ascending: false });
    }

    query = query.range((page - 1) * limit, page * limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
        items: data,
        total: count,
        page,
        totalPages: Math.ceil(count / limit)
    };
}

/**
 * Add item to content queue
 */
export async function addToContentQueue(item) {
    const { data, error } = await supabase
        .from('blog_content_queue')
        .insert({
            ...item,
            status: item.status || 'idea'
        })
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Update content queue item
 */
export async function updateContentQueueItem(id, updates) {
    const { data, error } = await supabase
        .from('blog_content_queue')
        .update({
            ...updates,
            updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Delete content queue item
 */
export async function deleteContentQueueItem(id) {
    const { error } = await supabase
        .from('blog_content_queue')
        .delete()
        .eq('id', id);

    if (error) throw error;
    return true;
}

/**
 * Move queue item to generation
 */
export async function processQueueItem(queueItemId) {
    // Get the queue item
    const { data: queueItem, error: fetchError } = await supabase
        .from('blog_content_queue')
        .select('*')
        .eq('id', queueItemId)
        .single();

    if (fetchError) throw fetchError;

    // Update status to generating
    await updateContentQueueItem(queueItemId, { status: 'generating' });

    // Create generation request
    const generation = await createGenerationRequest({
        topicId: queueItem.assigned_topic_id,
        triggerType: 'api',
        metadata: {
            from_queue: true,
            queue_item_id: queueItemId,
            content_brief: queueItem.content_brief,
            target_keywords: queueItem.target_keywords,
            suggested_headings: queueItem.suggested_headings
        }
    });

    return generation;
}

// =============================================================================
// SEO AUDITS
// =============================================================================

/**
 * Get SEO audit for a post
 */
export async function getSeoAudit(postId) {
    const { data, error } = await supabase
        .from('blog_seo_audits')
        .select('*')
        .eq('post_id', postId)
        .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
}

/**
 * Run SEO audit for a post
 */
export async function runSeoAudit(postId) {
    // Get the post
    const { data: post, error: postError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', postId)
        .single();

    if (postError) throw postError;

    // Calculate various SEO metrics
    const content = post.content_md || post.content_html || '';
    const plainText = content.replace(/<[^>]*>/g, '').replace(/[#*_`]/g, '');

    // Word and sentence analysis
    const words = plainText.split(/\s+/).filter(w => w.length > 0);
    const sentences = plainText.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 0);

    const wordCount = words.length;
    const sentenceCount = sentences.length;
    const paragraphCount = paragraphs.length;
    const avgSentenceLength = sentenceCount > 0 ? wordCount / sentenceCount : 0;

    // Flesch reading ease score
    const syllables = words.reduce((sum, word) => sum + countSyllables(word), 0);
    const fleschScore = 206.835 - (1.015 * avgSentenceLength) - (84.6 * (syllables / wordCount));

    // Heading analysis
    const h1Count = (content.match(/^# [^#]/gm) || []).length;
    const h2Count = (content.match(/^## [^#]/gm) || []).length;
    const h3Count = (content.match(/^### [^#]/gm) || []).length;
    const h4Count = (content.match(/^#### [^#]/gm) || []).length;

    // Link analysis
    const internalLinks = (content.match(/\[([^\]]+)\]\(\/[^)]+\)/g) || []);
    const externalLinks = (content.match(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g) || []);

    // Image analysis
    const images = (content.match(/!\[([^\]]*)\]\([^)]+\)/g) || []);
    const imagesWithAlt = images.filter(img => {
        const altMatch = img.match(/!\[([^\]]+)\]/);
        return altMatch && altMatch[1].trim().length > 0;
    });

    // Keyword analysis
    const primaryKeyword = post.keywords?.[0] || post.meta_title?.split(' ').slice(0, 3).join(' ') || '';
    const keywordCount = primaryKeyword ? (plainText.toLowerCase().match(new RegExp(primaryKeyword.toLowerCase(), 'g')) || []).length : 0;
    const keywordDensity = wordCount > 0 ? (keywordCount / wordCount) * 100 : 0;

    // Score calculations
    const scores = calculateSeoScores({
        title: post.title,
        metaTitle: post.meta_title,
        metaDescription: post.meta_description,
        wordCount,
        avgSentenceLength,
        fleschScore,
        keywords: post.keywords,
        keywordDensity,
        h1Count,
        h2Count,
        internalLinks: internalLinks.length,
        externalLinks: externalLinks.length,
        images: images.length,
        imagesWithAlt: imagesWithAlt.length,
        slug: post.slug,
        excerpt: post.excerpt
    });

    // Generate issues and recommendations
    const { issues, recommendations } = generateSeoRecommendations(scores, {
        title: post.title,
        metaTitle: post.meta_title,
        metaDescription: post.meta_description,
        wordCount,
        keywordDensity,
        primaryKeyword,
        h1Count,
        h2Count,
        internalLinks: internalLinks.length,
        externalLinks: externalLinks.length,
        images: images.length,
        imagesWithAlt: imagesWithAlt.length,
        fleschScore
    });

    // Calculate overall score
    const overallScore = Math.round(
        (scores.titleScore * 0.12) +
        (scores.metaDescriptionScore * 0.12) +
        (scores.keywordDensityScore * 0.10) +
        (scores.readabilityScore * 0.10) +
        (scores.internalLinksScore * 0.08) +
        (scores.externalLinksScore * 0.05) +
        (scores.imageAltScore * 0.08) +
        (scores.headingStructureScore * 0.10) +
        (scores.contentLengthScore * 0.15) +
        (scores.urlScore * 0.10)
    );

    // Upsert audit - use a simpler approach that works even if some columns are missing
    const auditData = {
        post_id: postId,
        overall_score: overallScore,
        title_score: scores.titleScore,
        meta_description_score: scores.metaDescriptionScore,
        keyword_density_score: scores.keywordDensityScore,
        readability_score: scores.readabilityScore,
        internal_links_score: scores.internalLinksScore,
        external_links_score: scores.externalLinksScore,
        image_alt_score: scores.imageAltScore,
        heading_structure_score: scores.headingStructureScore,
        content_length_score: scores.contentLengthScore,
        url_score: scores.urlScore,
        word_count: wordCount,
        sentence_count: sentenceCount,
        paragraph_count: paragraphCount,
        avg_sentence_length: avgSentenceLength,
        flesch_reading_score: fleschScore,
        keyword_count: keywordCount,
        primary_keyword: primaryKeyword,
        keyword_density: keywordDensity,
        heading_count: { h1: h1Count, h2: h2Count, h3: h3Count, h4: h4Count },
        internal_link_count: internalLinks.length,
        external_link_count: externalLinks.length,
        image_count: images.length,
        images_with_alt: imagesWithAlt.length,
        issues,
        recommendations
    };

    let audit;
    let auditError;

    // First try: check if audit exists
    const { data: existingAudit } = await supabase
        .from('blog_seo_audits')
        .select('id')
        .eq('post_id', postId)
        .single();

    if (existingAudit) {
        // Update existing audit
        const result = await supabase
            .from('blog_seo_audits')
            .update(auditData)
            .eq('post_id', postId)
            .select()
            .single();
        audit = result.data;
        auditError = result.error;
    } else {
        // Insert new audit
        const result = await supabase
            .from('blog_seo_audits')
            .insert(auditData)
            .select()
            .single();
        audit = result.data;
        auditError = result.error;
    }

    if (auditError) throw auditError;

    // Update post's last audit timestamp and SEO score
    await supabase
        .from('blog_posts')
        .update({
            last_seo_audit_at: new Date().toISOString(),
            seo_score: overallScore
        })
        .eq('id', postId);

    return audit;
}

/**
 * Count syllables in a word (simplified)
 */
function countSyllables(word) {
    word = word.toLowerCase();
    if (word.length <= 3) return 1;

    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
    word = word.replace(/^y/, '');

    const matches = word.match(/[aeiouy]{1,2}/g);
    return matches ? matches.length : 1;
}

/**
 * Calculate individual SEO scores
 */
function calculateSeoScores(data) {
    return {
        titleScore: calculateTitleScore(data.title, data.metaTitle),
        metaDescriptionScore: calculateMetaDescriptionScore(data.metaDescription),
        keywordDensityScore: calculateKeywordDensityScore(data.keywordDensity, data.keywords),
        readabilityScore: calculateReadabilityScore(data.fleschScore, data.avgSentenceLength),
        internalLinksScore: calculateLinksScore(data.internalLinks, 'internal'),
        externalLinksScore: calculateLinksScore(data.externalLinks, 'external'),
        imageAltScore: calculateImageAltScore(data.images, data.imagesWithAlt),
        headingStructureScore: calculateHeadingScore(data.h1Count, data.h2Count),
        contentLengthScore: calculateContentLengthScore(data.wordCount),
        urlScore: calculateUrlScore(data.slug)
    };
}

function calculateTitleScore(title, metaTitle) {
    if (!title) return 0;
    const len = metaTitle?.length || title.length;
    if (len >= 30 && len <= 60) return 100;
    if (len >= 20 && len <= 70) return 70;
    if (len >= 10 && len <= 80) return 40;
    return 20;
}

function calculateMetaDescriptionScore(desc) {
    if (!desc) return 0;
    const len = desc.length;
    if (len >= 120 && len <= 160) return 100;
    if (len >= 100 && len <= 180) return 70;
    if (len >= 50 && len <= 200) return 40;
    return 20;
}

function calculateKeywordDensityScore(density, keywords) {
    if (!keywords || keywords.length === 0) return 30;
    if (density >= 1 && density <= 3) return 100;
    if (density >= 0.5 && density <= 4) return 70;
    if (density > 0) return 40;
    return 20;
}

function calculateReadabilityScore(flesch, avgSentence) {
    let score = 0;

    // Flesch score (higher is better, 60-70 is ideal)
    if (flesch >= 60 && flesch <= 70) score += 50;
    else if (flesch >= 50 && flesch <= 80) score += 35;
    else if (flesch >= 30) score += 20;
    else score += 10;

    // Average sentence length (15-20 words is ideal)
    if (avgSentence >= 15 && avgSentence <= 20) score += 50;
    else if (avgSentence >= 10 && avgSentence <= 25) score += 35;
    else if (avgSentence >= 5 && avgSentence <= 30) score += 20;
    else score += 10;

    return score;
}

function calculateLinksScore(count, type) {
    if (type === 'internal') {
        if (count >= 3) return 100;
        if (count >= 2) return 70;
        if (count >= 1) return 40;
        return 20;
    } else {
        if (count >= 2) return 100;
        if (count >= 1) return 60;
        return 30;
    }
}

function calculateImageAltScore(total, withAlt) {
    if (total === 0) return 50; // No images is neutral
    const ratio = withAlt / total;
    if (ratio >= 1) return 100;
    if (ratio >= 0.8) return 70;
    if (ratio >= 0.5) return 40;
    return 20;
}

function calculateHeadingScore(h1, h2) {
    let score = 0;

    // Exactly one H1
    if (h1 === 1) score += 50;
    else if (h1 === 0) score += 20;
    else score += 10; // Multiple H1s is bad

    // Multiple H2s for structure
    if (h2 >= 3) score += 50;
    else if (h2 >= 2) score += 35;
    else if (h2 >= 1) score += 20;
    else score += 10;

    return score;
}

function calculateContentLengthScore(wordCount) {
    if (wordCount >= 2000) return 100;
    if (wordCount >= 1500) return 90;
    if (wordCount >= 1000) return 70;
    if (wordCount >= 500) return 50;
    if (wordCount >= 300) return 30;
    return 10;
}

function calculateUrlScore(slug) {
    if (!slug) return 0;

    let score = 100;

    // Too long
    if (slug.length > 75) score -= 30;
    else if (slug.length > 60) score -= 10;

    // Contains numbers only
    if (/^\d+$/.test(slug)) score -= 40;

    // Has special characters
    if (/[^a-z0-9-]/.test(slug)) score -= 20;

    // Too short
    if (slug.length < 5) score -= 20;

    return Math.max(score, 0);
}

/**
 * Generate SEO recommendations
 */
function generateSeoRecommendations(scores, data) {
    const issues = [];
    const recommendations = [];

    // Title issues
    if (scores.titleScore < 70) {
        issues.push({
            type: 'title',
            severity: scores.titleScore < 40 ? 'high' : 'medium',
            message: 'Title length is not optimal',
            suggestion: 'Keep your title between 30-60 characters for best SEO results'
        });
    }

    // Meta description issues
    if (!data.metaDescription) {
        issues.push({
            type: 'meta_description',
            severity: 'high',
            message: 'Meta description is missing',
            suggestion: 'Add a compelling meta description between 120-160 characters'
        });
    } else if (scores.metaDescriptionScore < 70) {
        issues.push({
            type: 'meta_description',
            severity: 'medium',
            message: 'Meta description length is not optimal',
            suggestion: 'Adjust meta description to be between 120-160 characters'
        });
    }

    // Keyword issues
    if (scores.keywordDensityScore < 40) {
        issues.push({
            type: 'keywords',
            severity: 'medium',
            message: data.keywordDensity > 4 ? 'Keyword density too high (keyword stuffing)' : 'Keyword density too low',
            suggestion: 'Aim for 1-3% keyword density for optimal SEO'
        });
    }

    // Readability issues
    if (data.fleschScore < 50) {
        issues.push({
            type: 'readability',
            severity: 'medium',
            message: 'Content is difficult to read',
            suggestion: 'Use shorter sentences and simpler words to improve readability'
        });
    }

    // Content length issues
    if (data.wordCount < 500) {
        issues.push({
            type: 'content_length',
            severity: 'high',
            message: 'Content is too short',
            suggestion: 'Aim for at least 1000+ words for better SEO performance'
        });
    } else if (data.wordCount < 1000) {
        issues.push({
            type: 'content_length',
            severity: 'medium',
            message: 'Content could be longer',
            suggestion: 'Consider expanding to 1500+ words for comprehensive coverage'
        });
    }

    // Internal links issues
    if (data.internalLinks < 2) {
        issues.push({
            type: 'internal_links',
            severity: 'medium',
            message: 'Not enough internal links',
            suggestion: 'Add 2-3 internal links to related content'
        });
    }

    // Image alt issues
    if (data.images > 0 && data.imagesWithAlt < data.images) {
        issues.push({
            type: 'image_alt',
            severity: 'medium',
            message: `${data.images - data.imagesWithAlt} image(s) missing alt text`,
            suggestion: 'Add descriptive alt text to all images'
        });
    }

    // Heading structure issues
    if (data.h1Count === 0) {
        issues.push({
            type: 'heading_structure',
            severity: 'high',
            message: 'Missing H1 heading',
            suggestion: 'Add exactly one H1 heading at the beginning of your content'
        });
    } else if (data.h1Count > 1) {
        issues.push({
            type: 'heading_structure',
            severity: 'medium',
            message: 'Multiple H1 headings found',
            suggestion: 'Use only one H1 heading per page'
        });
    }

    if (data.h2Count < 2) {
        issues.push({
            type: 'heading_structure',
            severity: 'low',
            message: 'Needs more H2 subheadings',
            suggestion: 'Add more H2 subheadings to improve content structure'
        });
    }

    // Generate positive recommendations
    if (scores.contentLengthScore >= 90) {
        recommendations.push({
            type: 'positive',
            message: 'Great content length! Long-form content performs well in search results.'
        });
    }

    if (scores.readabilityScore >= 80) {
        recommendations.push({
            type: 'positive',
            message: 'Excellent readability score. Your content is easy to understand.'
        });
    }

    if (data.internalLinks >= 3) {
        recommendations.push({
            type: 'positive',
            message: 'Good internal linking strategy.'
        });
    }

    return { issues, recommendations };
}

/**
 * Get posts needing SEO audit
 */
export async function getPostsNeedingAudit({ limit = 10 }) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, created_at, last_seo_audit_at, seo_score')
        .or(`last_seo_audit_at.is.null,last_seo_audit_at.lt.${thirtyDaysAgo.toISOString()}`)
        .order('last_seo_audit_at', { ascending: true, nullsFirst: true })
        .limit(limit);

    if (error) throw error;
    return data;
}

// =============================================================================
// KEYWORDS
// =============================================================================

/**
 * Get tracked keywords
 */
export async function getKeywords({
    page = 1,
    limit = 50,
    search = null,
    sortBy = 'search_volume'
}) {
    let query = supabase
        .from('blog_keywords')
        .select('*', { count: 'exact' });

    if (search) {
        query = query.ilike('keyword', `%${search}%`);
    }

    if (sortBy === 'search_volume') {
        query = query.order('search_volume', { ascending: false, nullsFirst: false });
    } else if (sortBy === 'difficulty') {
        query = query.order('difficulty', { ascending: true });
    } else {
        query = query.order('created_at', { ascending: false });
    }

    query = query.range((page - 1) * limit, page * limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
        items: data,
        total: count,
        page,
        totalPages: Math.ceil(count / limit)
    };
}

/**
 * Add or update a keyword
 */
export async function upsertKeyword(keyword) {
    const { data, error } = await supabase
        .from('blog_keywords')
        .upsert({
            ...keyword,
            updated_at: new Date().toISOString()
        }, {
            onConflict: 'keyword'
        })
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Delete a keyword
 */
export async function deleteKeyword(id) {
    const { error } = await supabase
        .from('blog_keywords')
        .delete()
        .eq('id', id);

    if (error) throw error;
    return true;
}

/**
 * Suggest keywords based on topic
 */
export async function suggestKeywords(topic) {
    // This would typically call an external API or AI service
    // For now, return basic suggestions
    const baseKeywords = topic.toLowerCase().split(' ');
    const suggestions = [
        topic,
        `${topic} guide`,
        `how to ${topic}`,
        `best ${topic}`,
        `${topic} tips`,
        `${topic} for beginners`,
        `${topic} 2024`,
        `${topic} examples`
    ].map(kw => ({
        keyword: kw,
        source: 'ai_suggested'
    }));

    return suggestions;
}

// =============================================================================
// ANALYTICS
// =============================================================================

/**
 * Get analytics overview
 */
export async function getAnalyticsOverview({ days = 30 }) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
        .from('blog_post_analytics')
        .select('*')
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

    if (error) throw error;

    // Aggregate data by date
    const byDate = {};
    data.forEach(row => {
        if (!byDate[row.date]) {
            byDate[row.date] = { views: 0, unique_visitors: 0 };
        }
        byDate[row.date].views += row.views || 0;
        byDate[row.date].unique_visitors += row.unique_visitors || 0;
    });

    const dailyData = Object.entries(byDate).map(([date, stats]) => ({
        date,
        ...stats
    }));

    const totalViews = dailyData.reduce((sum, d) => sum + d.views, 0);
    const totalVisitors = dailyData.reduce((sum, d) => sum + d.unique_visitors, 0);

    return {
        dailyData,
        totalViews,
        totalVisitors,
        avgDailyViews: Math.round(totalViews / days),
        period: { start: startDate.toISOString(), days }
    };
}

/**
 * Get top performing posts
 */
export async function getTopPosts({ limit = 10, metric = 'views', days = 30 }) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
        .from('blog_posts')
        .select(`
      id, title, slug, views, created_at, seo_score,
      topic:blog_topics(id, title)
    `)
        .order(metric, { ascending: false })
        .limit(limit);

    if (error) throw error;
    return data;
}

/**
 * Get post analytics
 */
export async function getPostAnalytics(postId, { days = 30 }) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
        .from('blog_post_analytics')
        .select('*')
        .eq('post_id', postId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

    if (error) throw error;

    return {
        dailyData: data,
        totalViews: data.reduce((sum, d) => sum + (d.views || 0), 0),
        avgTimeOnPage: data.length > 0
            ? Math.round(data.reduce((sum, d) => sum + (d.avg_time_on_page || 0), 0) / data.length)
            : 0
    };
}

/**
 * Track page view (for public use)
 */
export async function trackPageView(postId) {
    const { error } = await supabase.rpc('increment_post_views', {
        p_post_id: postId
    });

    if (error) {
        console.error('Error tracking page view:', error);
    }
}

// =============================================================================
// UNSPLASH INTEGRATION
// =============================================================================

/**
 * Get Unsplash API key safely
 */
function getUnsplashAccessKey() {
    try {
        return import.meta?.env?.VITE_UNSPLASH_ACCESS_KEY || '';
    } catch {
        return '';
    }
}

/**
 * Search Unsplash for images
 */
export async function searchUnsplashImages(query, { page = 1, perPage = 20 } = {}) {
    const UNSPLASH_ACCESS_KEY = getUnsplashAccessKey();
    if (!UNSPLASH_ACCESS_KEY) {
        console.warn('Unsplash API key not configured. Add VITE_UNSPLASH_ACCESS_KEY to your .env file.');
        return { results: [], total: 0, total_pages: 0 };
    }

    try {
        const response = await fetch(
            `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}`,
            {
                headers: {
                    'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`
                }
            }
        );

        if (!response.ok) {
            throw new Error('Unsplash API error');
        }

        const data = await response.json();

        // Cache results
        for (const photo of data.results) {
            await cacheUnsplashImage(photo, query);
        }

        return data;
    } catch (error) {
        console.error('Error searching Unsplash:', error);
        throw error;
    }
}

/**
 * Cache Unsplash image in database
 */
async function cacheUnsplashImage(photo, query) {
    const { error } = await supabase
        .from('blog_image_cache')
        .upsert({
            unsplash_id: photo.id,
            query,
            url_raw: photo.urls.raw,
            url_regular: photo.urls.regular,
            url_small: photo.urls.small,
            url_thumb: photo.urls.thumb,
            alt_text: photo.alt_description,
            photographer_name: photo.user.name,
            photographer_url: photo.user.links.html,
            download_location: photo.links.download_location,
            width: photo.width,
            height: photo.height,
            color: photo.color,
            blur_hash: photo.blur_hash
        }, {
            onConflict: 'unsplash_id'
        });

    if (error) {
        console.error('Error caching Unsplash image:', error);
    }
}

/**
 * Track Unsplash download (required by Unsplash API)
 */
export async function trackUnsplashDownload(downloadLocation) {
    const UNSPLASH_ACCESS_KEY = getUnsplashAccessKey();
    if (!UNSPLASH_ACCESS_KEY) return;

    try {
        await fetch(downloadLocation, {
            headers: {
                'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`
            }
        });
    } catch (error) {
        console.error('Error tracking Unsplash download:', error);
    }
}

/**
 * Get cached images
 */
export async function getCachedImages(query, { limit = 20 } = {}) {
    const { data, error } = await supabase
        .from('blog_image_cache')
        .select('*')
        .ilike('query', `%${query}%`)
        .limit(limit);

    if (error) throw error;
    return data;
}

// =============================================================================
// POST MANAGEMENT
// =============================================================================

/**
 * Get posts with filters
 */
export async function getPosts({
    page = 1,
    limit = 20,
    status = null,
    topicId = null,
    search = null,
    sortBy = 'created_at'
}) {
    let query = supabase
        .from('blog_posts')
        .select(`
      *,
      topic:blog_topics(id, title)
    `, { count: 'exact' });

    if (status) {
        if (status === 'published') {
            query = query.eq('is_published', true);
        } else {
            query = query.eq('status', status);
        }
    }

    if (topicId) {
        query = query.eq('topic_id', topicId);
    }

    if (search) {
        query = query.or(`title.ilike.%${search}%,content_md.ilike.%${search}%`);
    }

    query = query
        .order(sortBy, { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
        items: data,
        total: count,
        page,
        totalPages: Math.ceil(count / limit)
    };
}

/**
 * Update post
 */
export async function updatePost(postId, updates) {
    const { data, error } = await supabase
        .from('blog_posts')
        .update({
            ...updates,
            updated_at: new Date().toISOString()
        })
        .eq('id', postId)
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Schedule post for publishing
 */
export async function schedulePost(postId, publishAt) {
    return updatePost(postId, {
        status: 'scheduled',
        scheduled_publish_at: publishAt,
        is_published: false
    });
}

/**
 * Publish post immediately
 */
export async function publishPost(postId) {
    // Only include published_at if the column exists
    const updates = {
        status: 'published',
        is_published: true,
        scheduled_publish_at: null
    };

    // Try with published_at first, fallback without it
    try {
        const { data, error } = await supabase
            .from('blog_posts')
            .update({
                ...updates,
                published_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            })
            .eq('id', postId)
            .select()
            .single();

        if (error) {
            // If published_at column doesn't exist, retry without it
            if (error.code === 'PGRST204' && error.message.includes('published_at')) {
                return updatePost(postId, updates);
            }
            throw error;
        }
        return data;
    } catch (err) {
        // Fallback to basic update
        return updatePost(postId, updates);
    }
}

/**
 * Unpublish post
 */
export async function unpublishPost(postId) {
    return updatePost(postId, {
        status: 'draft',
        is_published: false
    });
}

/**
 * Delete post
 */
export async function deletePost(postId) {
    const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId);

    if (error) throw error;
    return true;
}

/**
 * Duplicate post as draft
 */
export async function duplicatePost(postId) {
    const { data: original, error: fetchError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', postId)
        .single();

    if (fetchError) throw fetchError;

    // Remove ID and timestamps, set as draft
    const { id, created_at, updated_at, published_at, ...postData } = original;

    const { data: newPost, error: insertError } = await supabase
        .from('blog_posts')
        .insert({
            ...postData,
            title: `${original.title} (Copy)`,
            slug: `${original.slug}-copy-${Date.now()}`,
            status: 'draft',
            is_published: false,
            views: 0,
            unique_views: 0
        })
        .select()
        .single();

    if (insertError) throw insertError;
    return newPost;
}

// =============================================================================
// AI GENERATION (Enhanced)
// =============================================================================

/**
 * Generate blog post with enhanced prompts
 */
export async function generateBlogPost({
    topicId,
    contentBrief = null,
    targetKeywords = [],
    suggestedHeadings = [],
    contentLength = 'medium',
    tone = 'professional',
    includeImages = true,
    includeFaqs = true
}) {
    // Create generation record
    const generation = await createGenerationRequest({
        topicId,
        triggerType: 'manual',
        metadata: {
            contentBrief,
            targetKeywords,
            suggestedHeadings,
            contentLength,
            tone,
            includeImages,
            includeFaqs
        }
    });

    try {
        // Update status to generating
        await updateGenerationStatus(generation.id, {
            status: 'generating',
            started_at: new Date().toISOString()
        });

        // Call the blog-writer edge function
        const { data, error } = await supabase.functions.invoke('blog-writer', {
            body: {
                topicId,
                contentBrief,
                targetKeywords,
                suggestedHeadings,
                contentLength,
                tone,
                includeImages,
                includeFaqs,
                generationId: generation.id
            }
        });

        if (error) throw error;

        // Check if generation was actually successful (has a postId)
        const hasError = data.results?.[0]?.error;
        const postId = data.postId || data.results?.[0]?.postId;

        if (hasError || !postId) {
            // Generation failed - update status to failed
            const errorMessage = hasError || 'No content generated from AI';
            await updateGenerationStatus(generation.id, {
                status: 'failed',
                error_message: errorMessage,
                completed_at: new Date().toISOString(),
                generation_time_ms: Date.now() - new Date(generation.created_at).getTime()
            });

            throw new Error(errorMessage);
        }

        // Update generation record - success
        await updateGenerationStatus(generation.id, {
            status: 'completed',
            post_id: postId,
            completed_at: new Date().toISOString(),
            generation_time_ms: Date.now() - new Date(generation.created_at).getTime(),
            content_length: data.wordCount || data.results?.[0]?.wordCount
        });

        // Run SEO audit on the new post
        if (postId) {
            await runSeoAudit(postId);
        }

        return {
            success: true,
            postId: postId,
            generationId: generation.id
        };
    } catch (error) {
        // Update generation record with error
        await updateGenerationStatus(generation.id, {
            status: 'failed',
            error_message: error.message,
            completed_at: new Date().toISOString()
        });

        throw error;
    }
}

/**
 * Regenerate specific sections of a post
 */
export async function regenerateSection(postId, section) {
    const { data: post, error: fetchError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', postId)
        .single();

    if (fetchError) throw fetchError;

    // Call edge function to regenerate specific section
    const { data, error } = await supabase.functions.invoke('blog-writer', {
        body: {
            action: 'regenerate_section',
            postId,
            section, // 'intro', 'conclusion', 'faqs', etc.
            existingContent: post.content_md
        }
    });

    if (error) throw error;

    return data;
}

// =============================================================================
// TOPICS MANAGEMENT
// =============================================================================

/**
 * Get topics with stats
 */
export async function getTopicsWithStats() {
    const { data, error } = await supabase
        .from('blog_topics')
        .select(`
      *,
      posts:blog_posts(count)
    `)
        .order('priority', { ascending: false })
        .order('title', { ascending: true });

    if (error) throw error;
    return data;
}

/**
 * Update topic
 */
export async function updateTopic(topicId, updates) {
    const { data, error } = await supabase
        .from('blog_topics')
        .update({
            ...updates,
            updated_at: new Date().toISOString()
        })
        .eq('id', topicId)
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Create topic
 */
export async function createTopic(topic) {
    const { data, error } = await supabase
        .from('blog_topics')
        .insert(topic)
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Delete topic
 */
export async function deleteTopic(topicId) {
    const { error } = await supabase
        .from('blog_topics')
        .delete()
        .eq('id', topicId);

    if (error) throw error;
    return true;
}

export default {
    // Stats
    getCommandCenterStats,

    // Scheduling
    getScheduleConfig,
    updateScheduleConfig,
    toggleSchedule,

    // Generation
    getGenerationHistory,
    createGenerationRequest,
    updateGenerationStatus,
    generateBlogPost,
    regenerateSection,

    // Content Queue
    getContentQueue,
    addToContentQueue,
    updateContentQueueItem,
    deleteContentQueueItem,
    processQueueItem,

    // SEO
    getSeoAudit,
    runSeoAudit,
    getPostsNeedingAudit,

    // Keywords
    getKeywords,
    upsertKeyword,
    deleteKeyword,
    suggestKeywords,

    // Analytics
    getAnalyticsOverview,
    getTopPosts,
    getPostAnalytics,
    trackPageView,

    // Unsplash
    searchUnsplashImages,
    trackUnsplashDownload,
    getCachedImages,

    // Posts
    getPosts,
    updatePost,
    schedulePost,
    publishPost,
    unpublishPost,
    deletePost,
    duplicatePost,

    // Topics
    getTopicsWithStats,
    updateTopic,
    createTopic,
    deleteTopic
};